var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useState, useRef, useImperativeHandle, forwardRef, } from 'react';
import shortid from 'shortid';
import { Title, Message, MessageWrapper } from '../../input';
import dayjs, { isValidDateStr } from '../date/day';
import { Container } from '../date/DatePicker';
import DateRangeInput from '../date/Input/DateRangeInput';
import { getDefaultPlaceholders, getDefaultClearTooltip, getDefaultInputTooltip, getCalendarBottomPosition, } from '../date';
import { DateTimeCalendarContainer } from './DateTimePicker';
import Calendar from './Calendar';
import { HOUR_FORMAT as DEFAULT_HOUR_FORMAT } from '.';
const DateTimeRangePicker = (_a, ref) => {
    var { lang = 'en', format = 'YYYY-MM-DD', fromRef = React.createRef(), toRef = React.createRef(), inputWidth = 9.375, status = 'valid', title, renderMessages = [], block = false, disabled, values, defaultValues, placeholders, onRangeChange, disabledDate, allowSame = true, minuteInterval, customHourFormat, forceDisabledValueFrom, forceDisableCalendarView, calendarAlign, calendarPosition = 'BOTTOM' } = _a, restProps = __rest(_a, ["lang", "format", "fromRef", "toRef", "inputWidth", "status", "title", "renderMessages", "block", "disabled", "values", "defaultValues", "placeholders", "onRangeChange", "disabledDate", "allowSame", "minuteInterval", "customHourFormat", "forceDisabledValueFrom", "forceDisableCalendarView", "calendarAlign", "calendarPosition"]);
    const HOUR_FORMAT = customHourFormat || DEFAULT_HOUR_FORMAT;
    const containerRef = useRef(null);
    const titleRef = useRef(null);
    const [open, setOpen] = useState(false); // indicate that calendar is opened or not
    const [isFocusOnToInput, setIsFocusOnToInput] = useState(false);
    const computeInputInitState = (index) => () => defaultValues && defaultValues.length === 2
        ? dayjs(lang, defaultValues[index]).format(`${format} ${HOUR_FORMAT}`)
        : '';
    const computeSelectedDateInitState = (index) => () => defaultValues && defaultValues.length === 2
        ? dayjs(lang, defaultValues[index])
        : undefined;
    const [inputValueFrom, setInputValueFrom] = useState(computeInputInitState(0));
    const [inputValueTo, setInputValueTo] = useState(computeInputInitState(1));
    const [selectedFrom, setSelectedFrom] = useState(computeSelectedDateInitState(0));
    const [selectedTo, setSelectedTo] = useState(computeSelectedDateInitState(1));
    let dateFrom;
    let dateTo;
    if (values && values.length === 2) {
        dateFrom = values[0] ? dayjs(lang, values[0]) : undefined;
        dateTo = values[1] ? dayjs(lang, values[1]) : undefined;
    }
    else {
        dateFrom = selectedFrom;
        dateTo = selectedTo;
    }
    const handleDateChange = useCallback((newDate) => {
        if (!fromRef || !fromRef.current || !toRef || !toRef.current)
            return;
        if (fromRef.current.matches(':focus')) {
            setSelectedFrom(newDate);
            if (newDate.isAfter(dayjs(lang, inputValueTo, format), 'date')) {
                // if start date is after end date means we need to reset to's Input for selecting next new end date
                setInputValueFrom((dateFrom ? newDate : newDate.hour(new Date().getHours())).format(`${format} ${HOUR_FORMAT}`));
                setInputValueTo('');
                toRef.current.focus();
                setSelectedTo(undefined);
                if (onRangeChange)
                    onRangeChange({ range: [newDate.toDate(), undefined] });
            }
            else if (dayjs(lang, inputValueFrom, `${format} ${HOUR_FORMAT}`).isValid()) {
                setInputValueFrom(newDate.format(`${format} ${HOUR_FORMAT}`));
                if (onRangeChange) {
                    onRangeChange({
                        range: [newDate.toDate(), dateTo ? dateTo.toDate() : undefined],
                    });
                }
                setOpen(false);
            }
            else {
                setInputValueFrom(newDate.format(format));
            }
        }
        else if (toRef.current.matches(':focus')) {
            setSelectedTo(newDate);
            if (dayjs(lang, inputValueTo, `${format} ${HOUR_FORMAT}`).isValid()) {
                setInputValueTo(newDate.format(`${format} ${HOUR_FORMAT}`));
                if (onRangeChange) {
                    onRangeChange({
                        range: [
                            dateFrom ? dateFrom.toDate() : undefined,
                            newDate.toDate(),
                        ],
                    });
                }
                setOpen(false);
            }
            else {
                setInputValueTo(newDate.format(format));
            }
        }
    }, [
        fromRef,
        toRef,
        lang,
        inputValueTo,
        format,
        inputValueFrom,
        dateFrom,
        onRangeChange,
        dateTo,
    ]);
    const handleTimeChange = useCallback((newDate) => {
        if (!fromRef || !fromRef.current || !toRef || !toRef.current)
            return;
        if (fromRef.current.matches(':focus')) {
            setSelectedFrom(newDate);
            setInputValueFrom(newDate.format(`${format} ${HOUR_FORMAT}`));
            if (!dateTo) {
                toRef.current.focus();
                return;
            }
            if (onRangeChange) {
                onRangeChange({ range: [newDate.toDate(), dateTo.toDate()] });
            }
        }
        else if (toRef.current.matches(':focus')) {
            setSelectedTo(newDate);
            setInputValueTo(newDate.format(`${format} ${HOUR_FORMAT}`));
            if (!dateFrom) {
                fromRef.current.focus();
                return;
            }
            if (onRangeChange) {
                onRangeChange({ range: [dateFrom.toDate(), newDate.toDate()] });
            }
        }
        setOpen(false);
    }, [fromRef, toRef, format, dateFrom, dateTo, onRangeChange]);
    const handleInputChange = useCallback((e) => {
        if (!fromRef || !fromRef.current || !toRef || !toRef.current)
            return;
        if (fromRef.current === e.target) {
            setInputValueFrom(e.target.value);
        }
        else if (toRef.current === e.target) {
            setInputValueTo(e.target.value);
        }
        const newDate = dayjs(lang, e.target.value, `${format} ${HOUR_FORMAT}`);
        const isValidDate = newDate.isValid() &&
            isValidDateStr(e.target.value, `${format} ${HOUR_FORMAT}`) &&
            !(disabledDate && disabledDate(newDate.toDate()));
        if (!isValidDate)
            return;
        if (fromRef.current === e.target) {
            setSelectedFrom(newDate);
            if (onRangeChange && dateTo)
                onRangeChange({ range: [newDate.toDate(), dateTo.toDate()] });
        }
        else if (toRef.current === e.target) {
            setSelectedTo(newDate);
            if (onRangeChange && dateFrom)
                onRangeChange({ range: [dateFrom.toDate(), newDate.toDate()] });
        }
    }, [
        format,
        lang,
        dateFrom,
        dateTo,
        fromRef,
        toRef,
        disabledDate,
        onRangeChange,
    ]);
    const handleClearBtnClick = useCallback(() => {
        setSelectedFrom(undefined);
        setSelectedTo(undefined);
        setInputValueFrom('');
        setInputValueTo('');
        setOpen(true);
        if (fromRef && fromRef.current)
            fromRef.current.focus();
        if (onRangeChange)
            onRangeChange({ range: undefined });
    }, [fromRef, onRangeChange]);
    const clearDateRange = () => {
        setSelectedFrom(undefined);
        setSelectedTo(undefined);
        setInputValueFrom('');
        setInputValueTo('');
        setOpen(false);
    };
    useImperativeHandle(ref, () => ({ clearDateRange }));
    const handleInputClick = useCallback(() => setOpen(true), []);
    useEffect(() => {
        if (!fromRef || !fromRef.current || !toRef || !toRef.current)
            return () => { };
        const fromInput = fromRef.current;
        const toInput = toRef.current;
        const handleOutsideClick = (e) => {
            // update new valid date when input blur (clicking outside)
            if (e.type === 'mousedown' &&
                (!containerRef.current ||
                    containerRef.current.contains(e.target) ||
                    !e.target.isConnected)) {
                return;
            }
            setOpen(false); // close calendar view
            const from = dayjs(lang, inputValueFrom, `${format} ${HOUR_FORMAT}`);
            const to = dayjs(lang, inputValueTo, `${format} ${HOUR_FORMAT}`);
            // check range is a invalid format or disabled date
            const isValidRange = from.isValid() &&
                to.isValid() &&
                isValidDateStr(inputValueFrom, `${format} ${HOUR_FORMAT}`) &&
                isValidDateStr(inputValueTo, `${format} ${HOUR_FORMAT}`) &&
                (from.isBefore(to, 'hour') ||
                    from.isBefore(to, 'minute') ||
                    (allowSame &&
                        from.isSameOrBefore(to, 'hour') &&
                        from.isSameOrBefore(to, 'minute'))) &&
                !(disabledDate &&
                    ((disabledDate(from.toDate()) && !forceDisabledValueFrom) ||
                        disabledDate(to.toDate())));
            if (!isValidRange) {
                // If the focus state is not cleared, it will trigger the TimeSelector useEffect,
                // then the SelectedFrom value will be written to InputValueTo
                fromInput.blur();
                toInput.blur();
                // drop invalid range
                if (values && onRangeChange)
                    onRangeChange({ range: undefined });
                setIsFocusOnToInput(false);
                setSelectedFrom(undefined);
                setSelectedTo(undefined);
                setInputValueFrom('');
                setInputValueTo('');
            }
        };
        const focusToNext = (e) => {
            const keyCode = e.keyCode || e.which;
            if (keyCode === 13)
                toInput.focus();
        };
        const goBlur = (e) => {
            const keyCode = e.keyCode || e.which;
            if ((e.type === 'keydown' && keyCode === 9) ||
                (e.type === 'keyup' && keyCode === 13)) {
                // tab or enter
                toInput.blur();
                handleOutsideClick(e);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        fromInput.addEventListener('keyup', focusToNext);
        toInput.addEventListener('keydown', goBlur);
        toInput.addEventListener('keyup', goBlur);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            fromInput.removeEventListener('keyup', focusToNext);
            toInput.removeEventListener('keydown', goBlur);
            toInput.removeEventListener('keyup', goBlur);
        };
    }, [
        lang,
        format,
        fromRef,
        toRef,
        inputValueFrom,
        inputValueTo,
        values,
        onRangeChange,
        disabledDate,
        allowSame,
    ]);
    useEffect(() => {
        // focus to another input won't get latest disabledDate
        // so we update a state to trigger Calendar component re-render
        if (!toRef || !toRef.current)
            return () => { };
        const toElement = toRef.current;
        const handleFocus = () => setIsFocusOnToInput(true);
        const handleBlur = () => setIsFocusOnToInput(false);
        toElement.addEventListener('focus', handleFocus);
        toElement.addEventListener('blur', handleBlur);
        return () => {
            toElement.removeEventListener('focus', handleFocus);
            toElement.removeEventListener('blur', handleBlur);
        };
    }, [toRef]);
    useEffect(() => {
        if (!fromRef || !fromRef.current || !toRef || !toRef.current)
            return;
        if (fromRef.current.matches(':focus')) {
            const to = dayjs(lang, inputValueTo, `${format} ${HOUR_FORMAT}`);
            if (dateTo && !to.isValid()) {
                setInputValueTo(dateTo.hour(new Date().getHours()).format(`${format} ${HOUR_FORMAT}`));
            }
        }
        else if (toRef.current.matches(':focus')) {
            const from = dayjs(lang, inputValueFrom, `${format} ${HOUR_FORMAT}`);
            if (dateFrom && !from.isValid()) {
                setInputValueFrom(dateFrom
                    .hour(new Date().getHours())
                    .format(`${format} ${HOUR_FORMAT}`));
            }
        }
    }, [
        lang,
        format,
        fromRef,
        toRef,
        inputValueFrom,
        inputValueTo,
        dateFrom,
        dateTo,
    ]);
    useEffect(() => {
        let newDateFrom;
        let newDateTo;
        if (values && values.length === 2) {
            newDateFrom = values[0]
                ? dayjs(lang, values[0]).format(`${format} ${HOUR_FORMAT}`)
                : '';
            newDateTo = values[1]
                ? dayjs(lang, values[1]).format(`${format} ${HOUR_FORMAT}`)
                : '';
        }
        else {
            newDateFrom = inputValueFrom;
            newDateTo = inputValueTo;
        }
        setInputValueFrom(newDateFrom);
        setInputValueTo(newDateTo);
    }, [lang, format, values]); // eslint-disable-line react-hooks/exhaustive-deps
    return (React.createElement(Container, { ref: containerRef, block: block },
        title && (React.createElement(Title, { ref: titleRef, onClick: handleClearBtnClick }, title)),
        React.createElement(DateRangeInput, Object.assign({ lang: lang, fromRef: fromRef, toRef: toRef, selectedFrom: dateFrom, selectedTo: dateTo, valueFrom: inputValueFrom, valueTo: inputValueTo, width: inputWidth, status: status, block: block, disabled: disabled, placeholders: placeholders || getDefaultPlaceholders(lang), onChange: handleInputChange, onClick: forceDisableCalendarView ? () => { } : handleInputClick, onClearBtnClick: handleClearBtnClick, tooltipDirection: calendarPosition === 'TOP' ? 'BOTTOM_CENTER' : 'TOP_CENTER', tooltipText: getDefaultInputTooltip(lang).replace('{{format}}', minuteInterval ? `${format} am/pm` : `${format} Ham/pm`), clearBtnTooltipText: getDefaultClearTooltip(lang), forceDisabledValueFrom: forceDisabledValueFrom, forceDisableCalendarView: forceDisableCalendarView }, restProps)),
        React.createElement(MessageWrapper, null, renderMessages.map(renderMessage => (React.createElement(Message, { status: status, withBullet: renderMessages.length > 1, key: shortid.generate() }, renderMessage())))),
        React.createElement(DateTimeCalendarContainer, { top: title ? 4.25 : 2.75, bottom: getCalendarBottomPosition({
                containerRef,
                titleRef,
            }), open: !disabled && open, onMouseDown: e => e.preventDefault(), calendarAlign: calendarAlign, calendarPosition: calendarPosition },
            React.createElement(Calendar, { lang: lang, format: `${format} ${HOUR_FORMAT}`, open: !disabled && open, isFocusOnToInput: isFocusOnToInput, selectedFrom: dateFrom, selectedTo: dateTo, valueFrom: inputValueFrom, valueTo: inputValueTo, onDateChange: handleDateChange, onTimeChange: handleTimeChange, minuteInterval: minuteInterval, disabledDate: current => (isFocusOnToInput &&
                    dateFrom &&
                    dayjs(lang, current).isBefore(dateFrom, 'date')) ||
                    !!(disabledDate && disabledDate(current)) }))));
};
export default forwardRef(DateTimeRangePicker);
//# sourceMappingURL=DateTimeRangePicker.js.map